import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./CommonActionMenu.css";

function CommonActionMenu({ actions = [], ariaLabel = "Row actions" }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const visibleActions = actions.filter(Boolean);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const clickedInsideTrigger = menuRef.current?.contains(event.target);
      const clickedInsideDropdown = dropdownRef.current?.contains(event.target);

      if (!clickedInsideTrigger && !clickedInsideDropdown) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !triggerRef.current) {
      return undefined;
    }

    const updatePosition = () => {
      if (!triggerRef.current) {
        return;
      }

      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownWidth = 190;
      const dropdownHeight = Math.max(120, visibleActions.length * 44 + 16);
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 8;

      let top = rect.bottom + gap;
      const shouldOpenUpward =
        viewportHeight - rect.bottom < dropdownHeight + gap &&
        rect.top > dropdownHeight;

      if (shouldOpenUpward) {
        top = rect.top - dropdownHeight - gap;
      }

      let left = rect.right - dropdownWidth;
      left = Math.max(gap, Math.min(left, viewportWidth - dropdownWidth - gap));

      setDropdownStyle({
        position: "fixed",
        top: `${Math.max(gap, top)}px`,
        left: `${left}px`,
        width: `${dropdownWidth}px`,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, visibleActions.length]);

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div className="common-action-menu" ref={menuRef}>
      <button
        type="button"
        className="common-action-menu-trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        ref={triggerRef}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <i className="fas fa-ellipsis-v"></i>
      </button>

      {open &&
        createPortal(
          <div
            className="common-action-menu-dropdown"
            role="menu"
            style={dropdownStyle}
            ref={dropdownRef}
          >
            {visibleActions.map((action) => (
              <button
                key={action.label}
                type="button"
                className={`common-action-menu-item ${action.variant ? `is-${action.variant}` : ""}`.trim()}
                disabled={action.disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  setOpen(false);
                  action.onClick?.();
                }}
                role="menuitem"
              >
                {action.icon ? <i className={action.icon}></i> : null}
                <span className="common-action-menu-item-label">
                  {action.label}
                </span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

export default CommonActionMenu;
